import React, { useEffect } from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { ProductCompareTheme } from "../../../constants";
import { ISafeHTML, IWebPageURL } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
    IGroupColumnsHeader,
    isoTileID,
} from "../../../models/product-compare";
import { trackCategoryPageView } from "../../../utils/analytics";
import { notEmpty } from "../../../utils/functional";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { ProductSelectionGrid as ProductSelectionGridComponent } from "../components/ProductSelectionGrid";
import { defaults } from "../defaults";
import { addToSelected, removeFromSelected, showModal } from "../reducers";
import { getSelectedVariant } from "../selectors";
import { loadSelectedTileID } from "../thunks";
import { SelectedProductTray } from "./SelectedProductTray";

interface IProps {
    header: ISafeHTML;
    financingLink: IWebPageURL;
    ctaText?: string;
    preselectedSize: string | null;
    productComparePageURL: IWebPageURL | null;
    sizeAttributeOptionGroup: IAttributeOptionGroup | null;
    showStartingAt?: boolean;
    showRating?: boolean;
    showInStoreCallout?: boolean;
    theme: ProductCompareTheme;
    footer: ISafeHTML | null;
    groupHeader?: IGroupColumnsHeader | null;
    showSizeSelector?: boolean | null;
}

const trackCategoryView = (
    tiles: readonly ICompareTile[],
    priceSelectValue: string,
): void => {
    // Obtain SKUs for current variants
    const skus = tiles
        .map((tile) => {
            const variant = getSelectedVariant(tile, priceSelectValue);
            return variant ? variant.skus[0] : null;
        })
        .filter(notEmpty);
    trackCategoryPageView(skus);
};

const loadTilesFromURL = (
    dispatch: ReturnType<typeof useAppDispatch>,
): void => {
    const window = getWindow();
    if (!window) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("tiles")) {
        const selectedTileIDs = decodeURIComponent(urlParams.get("tiles") || "")
            .split(",")
            .map(Number)
            .map(isoTileID.wrap);
        if (selectedTileIDs.length < 5) {
            selectedTileIDs.forEach((tileID) => {
                void dispatch(loadSelectedTileID(tileID));
            });
        } else {
            urlParams.delete("tiles");
            const newURL = `${window.location.origin}${
                window.location.pathname
            }?${urlParams.toString()}`;
            window.history.replaceState(window.history.state, "", newURL);
        }
    }
};

const shouldShowCompareFunctionality = (
    theme: ProductCompareTheme,
): boolean => {
    return ![
        ProductCompareTheme.WITHOUT_COMPARE,
        ProductCompareTheme.THEME_ONE,
    ].includes(theme);
};

export const ProductSelectionGrid = (props: IProps): React.ReactNode => {
    const state = useAppSelector(
        (rootState) => rootState.productcompare || defaults,
    );
    const dispatch = useAppDispatch();

    const tiles = state.tiles;
    const selectedTileIDs = state.selectedTileIDs;
    const priceSelectValue = state.priceSelectValue;

    const hasLoadedTilesFromURLRef = React.useRef(false);
    useEffect(() => {
        if (hasLoadedTilesFromURLRef.current) {
            return;
        }
        hasLoadedTilesFromURLRef.current = true;
        loadTilesFromURL(dispatch);
    }, []);

    const hasRenderedOnceRef = React.useRef(false);
    useEffect(() => {
        if (!hasRenderedOnceRef.current) {
            hasRenderedOnceRef.current = true;
            return;
        }
        trackCategoryView(tiles, priceSelectValue);
    });

    const showCompareFunctionality = shouldShowCompareFunctionality(
        props.theme,
    );

    const handleAddToSelected = (
        tileID: ICompareTile["product_compare_tile"]["id"],
    ) => {
        dispatch(addToSelected({ tileID }));
    };

    const handleRemoveFromSelected = (
        tileID: ICompareTile["product_compare_tile"]["id"],
    ) => {
        dispatch(removeFromSelected({ tileID }));
    };

    const handleShowModal = () => {
        dispatch(showModal());
    };

    return (
        <>
            {showCompareFunctionality && (
                <SelectedProductTray
                    tiles={tiles}
                    productComparePageURL={props.productComparePageURL}
                    ctaText={props.ctaText}
                />
            )}
            <ProductSelectionGridComponent
                header={props.header}
                tiles={tiles}
                selectedTileIDs={selectedTileIDs}
                priceSelectValue={priceSelectValue}
                financingLink={props.financingLink}
                addToSelected={handleAddToSelected}
                removeFromSelected={handleRemoveFromSelected}
                showStartingAt={props.showStartingAt}
                showInStoreCallout={props.showInStoreCallout}
                showRating={props.showRating}
                ctaText={props.ctaText}
                showModal={handleShowModal}
                productComparePageURL={props.productComparePageURL}
                preselectedSize={props.preselectedSize || null}
                sizeAttributeOptionGroup={props.sizeAttributeOptionGroup}
                theme={props.theme}
                footer={props.footer}
                groupHeader={props.groupHeader}
                showSizeSelector={props.showSizeSelector}
            />
        </>
    );
};
